import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import NavigationBar from './shared/NavigationBar';
import Sidebar from './components/Sidebar';
import ResizeHandle from './components/ResizeHandle';
import MergeDialog from './components/MergeDialog';
import KeyboardShortcutsModal from './components/KeyboardShortcutsModal';
import QuickOpenModal from './components/QuickOpenModal';
import Editor from './pages/Editor';
import ProjectDocViewer from './components/ProjectDocViewer';
import { documentApi } from './services/documentApi';
import { projectApi } from './services/projectApi';
import './i18n';

// Protected route - admin only
const ProtectedRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F5F5F5]">
        <div className="text-[#999]">불러오는 중...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F5F5F5]">
        <div className="text-center">
          <p className="text-[#999] mb-4">로그인이 필요합니다 (관리자 전용)</p>
          <a
            href={`https://auth.ilouli.com/login?returnUrl=${encodeURIComponent(window.location.href)}`}
            className="px-6 py-2.5 bg-[#E16B5A] text-white rounded-lg hover:bg-[#D15A49] transition-colors inline-block text-sm font-medium"
          >
            로그인
          </a>
        </div>
      </div>
    );
  }

  if (user?.tier !== 'admin') {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F5F5F5]">
        <div className="text-center">
          <p className="text-[#999]">관리자 권한이 필요합니다</p>
        </div>
      </div>
    );
  }

  return children;
};

// Mobile detection hook
const MOBILE_BREAKPOINT = 768;
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
  useEffect(() => {
    let timer;
    const handleResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT), 150);
    };
    window.addEventListener('resize', handleResize);
    return () => { clearTimeout(timer); window.removeEventListener('resize', handleResize); };
  }, []);
  return isMobile;
};

// Main Bear-style layout
const BearLayout = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > MOBILE_BREAKPOINT);
  const [activeTag, setActiveTag] = useState(null);
  const navigate = useNavigate();
  const { id: activeId, projectName: urlProject, docId: urlDocId } = useParams();

  // Sort state (persisted in localStorage)
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('mb_sortBy') || 'updated');
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem('mb_sortOrder') || 'desc');

  // Trash mode
  const [trashMode, setTrashMode] = useState(false);
  const [trashCount, setTrashCount] = useState(0);

  // Multi-select
  const [selectedIds, setSelectedIds] = useState(new Set());
  const lastSelectedRef = useRef(null);

  // Merge dialog
  const [showMergeDialog, setShowMergeDialog] = useState(false);

  // Keyboard shortcuts modal
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Quick Open modal
  const [showQuickOpen, setShowQuickOpen] = useState(false);
  const [quickOpenInitialTab, setQuickOpenInitialTab] = useState(null);
  const [quickOpenInitialQuery, setQuickOpenInitialQuery] = useState('');

  // Tag metadata
  const [tagMetadata, setTagMetadata] = useState([]);

  // Project docs
  const [projectList, setProjectList] = useState([]);
  const [activeProjectDoc, setActiveProjectDoc] = useState(null);

  // Sync URL params → activeProjectDoc
  useEffect(() => {
    if (urlProject && urlDocId) {
      setActiveProjectDoc({ project: decodeURIComponent(urlProject), id: Number(urlDocId) });
    } else if (!urlProject) {
      // Not on a /project/ route, clear project doc selection
      setActiveProjectDoc(null);
    }
  }, [urlProject, urlDocId]);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await documentApi.list(1, 100, search, {
        sort: sortBy,
        order: sortOrder,
        trash: trashMode,
      });
      setDocuments(res.data || []);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setLoading(false);
    }
  }, [search, sortBy, sortOrder, trashMode]);

  // Fetch trash count
  const fetchTrashCount = useCallback(async () => {
    try {
      const res = await documentApi.list(1, 1, '', { trash: true });
      setTrashCount(res.pagination?.total || 0);
    } catch { /* ignore */ }
  }, []);

  // Fetch tag metadata
  const fetchTagMetadata = useCallback(async () => {
    try {
      const res = await documentApi.tagList();
      setTagMetadata(res.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    fetchTagMetadata();
  }, [fetchTagMetadata]);

  // Fetch project list
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await projectApi.listProjects();
        const projects = res.data || [];
        // For each project, fetch its docs for sidebar tree display
        const withDocs = await Promise.all(
          projects.map(async (proj) => {
            try {
              const docsRes = await projectApi.listDocs(proj.project);
              return { ...proj, docs: docsRes.data || [] };
            } catch {
              return { ...proj, docs: [] };
            }
          })
        );
        setProjectList(withDocs);
      } catch (err) {
        console.error('Failed to fetch projects:', err);
      }
    };
    fetchProjects();
  }, []);

  // Project doc select handler - navigate to URL + track open
  const handleProjectDocSelect = useCallback((project, docId) => {
    navigate(`/project/${encodeURIComponent(project)}/${docId}`);
    projectApi.markOpened(project, docId).catch(() => {});
    if (isMobile) setSidebarOpen(false);
  }, [navigate, isMobile]);

  useEffect(() => {
    if (!trashMode) fetchTrashCount();
  }, [trashMode, fetchTrashCount, documents]);

  // Sort change handler
  const handleSortChange = useCallback((newSort, newOrder) => {
    setSortBy(newSort);
    setSortOrder(newOrder);
    localStorage.setItem('mb_sortBy', newSort);
    localStorage.setItem('mb_sortOrder', newOrder);
  }, []);

  // Multi-select handlers
  const handleToggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      lastSelectedRef.current = id;
      return next;
    });
  }, []);

  const handleRangeSelect = useCallback((id) => {
    const lastId = lastSelectedRef.current;
    if (lastId === null) {
      handleToggleSelect(id);
      return;
    }
    const ids = documents.map(d => d.id);
    const startIdx = ids.indexOf(lastId);
    const endIdx = ids.indexOf(id);
    if (startIdx === -1 || endIdx === -1) return;

    const [from, to] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
    setSelectedIds(prev => {
      const next = new Set(prev);
      for (let i = from; i <= to; i++) next.add(ids[i]);
      return next;
    });
    lastSelectedRef.current = id;
  }, [documents, handleToggleSelect]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
    lastSelectedRef.current = null;
  }, []);

  // Document actions
  const handleNew = async () => {
    try {
      const res = await documentApi.create({ title: '', content: '' });
      setDocuments(docs => [res.data, ...docs]);
      navigate(`/editor/${res.data.id}`);
    } catch (err) {
      console.error('Failed to create document:', err);
    }
  };

  const handleNewChild = useCallback(async (parentId) => {
    try {
      const res = await documentApi.create({ title: '', content: '', parent_id: parentId });
      fetchDocuments();
      navigate(`/editor/${res.data.id}`);
    } catch (err) {
      console.error('Failed to create child document:', err);
    }
  }, [navigate, fetchDocuments]);

  const handleMove = useCallback(async (docId, parentId) => {
    try {
      await documentApi.move(docId, parentId);
      // If moving children to root when parent is deleted, update locally
      setDocuments(docs =>
        docs.map(d => d.id === docId ? { ...d, parent_id: parentId } : d)
      );
    } catch (err) {
      console.error('Failed to move document:', err);
    }
  }, []);

  const handleDelete = async (id) => {
    try {
      // Move children to root before deleting
      const children = documents.filter(d => d.parent_id === id);
      for (const child of children) {
        await documentApi.move(child.id, null);
      }
      await documentApi.delete(id);
      setDocuments(docs => docs.filter(d => d.id !== id).map(d =>
        d.parent_id === id ? { ...d, parent_id: null } : d
      ));
      if (String(activeId) === String(id)) navigate('/');
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleSelect = (id) => {
    setActiveProjectDoc(null);
    navigate(`/editor/${id}`);
    documentApi.markOpened(id).catch(() => {});
    if (isMobile) setSidebarOpen(false);
  };

  const handleQuickOpenSelect = (id) => {
    setActiveProjectDoc(null);
    navigate(`/editor/${id}`);
    documentApi.markOpened(id).catch(() => {});
    setShowQuickOpen(false);
    if (isMobile) setSidebarOpen(false);
  };

  const handleDocumentSaved = useCallback((updatedDoc) => {
    setDocuments(docs => {
      const exists = docs.some(d => d.id === updatedDoc.id);
      if (exists) {
        return docs.map(d => d.id === updatedDoc.id ? { ...d, ...updatedDoc } : d);
      }
      // 새 문서가 아직 목록에 없으면 추가
      return [updatedDoc, ...docs];
    });
  }, []);

  const handlePin = useCallback(async (id) => {
    try {
      const res = await documentApi.pin(id);
      setDocuments(docs =>
        docs.map(d => d.id === id ? { ...d, is_pinned: res.data.is_pinned } : d)
      );
    } catch (err) {
      console.error('Failed to pin:', err);
    }
  }, []);

  const handleDuplicate = useCallback(async (id) => {
    try {
      const res = await documentApi.duplicate(id);
      setDocuments(docs => [res.data, ...docs]);
      navigate(`/editor/${res.data.id}`);
    } catch (err) {
      console.error('Failed to duplicate:', err);
    }
  }, [navigate]);

  const handleExport = useCallback(async (id, title) => {
    try {
      await documentApi.exportMd(id, title);
    } catch (err) {
      console.error('Failed to export:', err);
    }
  }, []);

  const handleRestore = useCallback(async (id) => {
    try {
      await documentApi.restore(id);
      setDocuments(docs => docs.filter(d => d.id !== id));
    } catch (err) {
      console.error('Failed to restore:', err);
    }
  }, []);

  const handlePermanentDelete = useCallback(async (id) => {
    if (!window.confirm('이 문서를 영구 삭제하시겠습니까? 복구할 수 없습니다.')) return;
    try {
      await documentApi.permanentDelete(id);
      setDocuments(docs => docs.filter(d => d.id !== id));
    } catch (err) {
      console.error('Failed to permanently delete:', err);
    }
  }, []);

  const handleEmptyTrash = useCallback(async () => {
    try {
      await documentApi.emptyTrash();
      setDocuments([]);
    } catch (err) {
      console.error('Failed to empty trash:', err);
    }
  }, []);

  const handleTrashToggle = useCallback(() => {
    setTrashMode(prev => !prev);
    setSearch('');
    setActiveTag(null);
    handleClearSelection();
    if (activeId) navigate('/');
  }, [activeId, navigate, handleClearSelection]);

  // Merge
  const handleMergeRequest = useCallback(() => {
    if (selectedIds.size < 2) return;
    setShowMergeDialog(true);
  }, [selectedIds]);

  const handleMerge = useCallback(async (ids, targetId, separator) => {
    try {
      await documentApi.merge(ids, targetId, separator);
      setShowMergeDialog(false);
      handleClearSelection();
      fetchDocuments();
      navigate(`/editor/${targetId}`);
    } catch (err) {
      console.error('Failed to merge:', err);
    }
  }, [fetchDocuments, navigate, handleClearSelection]);

  // Tag management handlers
  const handleTagRename = useCallback(async (oldName, newName) => {
    try {
      await documentApi.tagRename(oldName, newName);
      fetchDocuments();
      fetchTagMetadata();
      if (activeTag === oldName) setActiveTag(newName);
    } catch (err) {
      console.error('Failed to rename tag:', err);
    }
  }, [fetchDocuments, fetchTagMetadata, activeTag]);

  const handleTagDelete = useCallback(async (name) => {
    try {
      await documentApi.tagDelete(name);
      fetchDocuments();
      fetchTagMetadata();
      if (activeTag === name) setActiveTag(null);
    } catch (err) {
      console.error('Failed to delete tag:', err);
    }
  }, [fetchDocuments, fetchTagMetadata, activeTag]);

  const handleTagMerge = useCallback(async (source, target) => {
    try {
      await documentApi.tagMerge(source, target);
      fetchDocuments();
      fetchTagMetadata();
      if (activeTag === source) setActiveTag(target);
    } catch (err) {
      console.error('Failed to merge tags:', err);
    }
  }, [fetchDocuments, fetchTagMetadata, activeTag]);

  const handleTagColor = useCallback(async (name, color) => {
    try {
      await documentApi.tagColor(name, color);
      fetchTagMetadata();
    } catch (err) {
      console.error('Failed to set tag color:', err);
    }
  }, [fetchTagMetadata]);

  // Filter documents by active tag (supports hierarchical: "work" matches "work/project")
  const filteredDocuments = activeTag
    ? documents.filter(doc => {
        let docTags = [];
        if (Array.isArray(doc.tags)) docTags = doc.tags;
        else if (typeof doc.tags === 'string') {
          try { docTags = JSON.parse(doc.tags); } catch { docTags = []; }
        }
        if (activeTag === '__untagged__') return docTags.length === 0;
        if (activeTag === '__pinned__') return doc.is_pinned;
        return docTags.some(t => t === activeTag || t.startsWith(activeTag + '/'));
      })
    : documents;

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMeta = e.metaKey || e.ctrlKey;

      // Escape - clear selection or close shortcuts
      if (e.key === 'Escape') {
        handleClearSelection();
        return;
      }

      // ? - show keyboard shortcuts (only when not typing)
      if (e.key === '?' && !e.metaKey && !e.ctrlKey) {
        const tag = document.activeElement?.tagName;
        if (tag !== 'INPUT' && tag !== 'TEXTAREA' && !document.activeElement?.closest('.cm-editor')) {
          e.preventDefault();
          setShowShortcuts(prev => !prev);
          return;
        }
      }

      // Cmd+P - Quick Open
      if (isMeta && e.key === 'p' && !e.shiftKey) {
        e.preventDefault();
        setShowQuickOpen(prev => !prev);
        return;
      }

      // Cmd+N - new note
      if (isMeta && e.key === 'n') {
        e.preventDefault();
        handleNew();
        return;
      }

      // Only when a document is active
      if (!activeId) return;
      const id = Number(activeId);
      const doc = documents.find(d => d.id === id);

      // Cmd+D - duplicate
      if (isMeta && e.key === 'd') {
        e.preventDefault();
        handleDuplicate(id);
        return;
      }

      // Cmd+Shift+P - pin toggle
      if (isMeta && e.shiftKey && e.key === 'p') {
        e.preventDefault();
        handlePin(id);
        return;
      }

      // Cmd+E - export
      if (isMeta && e.key === 'e') {
        e.preventDefault();
        handleExport(id, doc?.title);
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeId, documents, handleDuplicate, handlePin, handleExport, handleClearSelection]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get selected documents for merge dialog
  const selectedDocs = showMergeDialog
    ? documents.filter(d => selectedIds.has(d.id))
    : [];

  // Get current active document for editor props
  const activeDoc = documents.find(d => d.id === Number(activeId));

  // All unique tags for autocomplete
  const allTags = useMemo(() => {
    const tags = new Set();
    documents.forEach(doc => {
      let docTags = [];
      if (Array.isArray(doc.tags)) docTags = doc.tags;
      else if (typeof doc.tags === 'string') {
        try { docTags = JSON.parse(doc.tags); } catch { docTags = []; }
      }
      docTags.forEach(t => tags.add(t));
    });
    return [...tags].sort();
  }, [documents]);

  // Sidebar width (resizable, persisted)
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    return parseInt(localStorage.getItem('mb_sidebarWidth') || '280', 10);
  });

  const handleSidebarResize = useCallback((deltaX) => {
    setSidebarWidth(prev => Math.max(200, Math.min(480, prev + deltaX)));
  }, []);

  const handleResizeEnd = useCallback(() => {
    localStorage.setItem('mb_sidebarWidth', String(sidebarWidth));
  }, [sidebarWidth]);

  return (
    <div className="bear-layout">
      {/* Mobile sidebar overlay */}
      {isMobile && sidebarOpen && (
        <div className="bear-sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar (filters + tags + note list) */}
      {sidebarOpen && (
        <>
          <div
            className={isMobile ? 'bear-sidebar-mobile' : undefined}
            style={isMobile ? { width: '85vw', maxWidth: 360 } : { width: sidebarWidth, minWidth: sidebarWidth, flexShrink: 0 }}
          >
            <Sidebar
              documents={filteredDocuments}
              allDocuments={documents}
              loading={loading}
              search={search}
              onSearchChange={setSearch}
              activeId={activeId ? Number(activeId) : null}
              onSelect={handleSelect}
              onNew={handleNew}
              onNewChild={handleNewChild}
              onDelete={handleDelete}
              onPin={handlePin}
              onDuplicate={handleDuplicate}
              onExport={handleExport}
              onRestore={handleRestore}
              onPermanentDelete={handlePermanentDelete}
              onEmptyTrash={handleEmptyTrash}
              onMove={handleMove}
              onMerge={handleMergeRequest}
              activeTag={activeTag}
              onTagSelect={setActiveTag}
              isOpen={sidebarOpen}
              onToggle={() => setSidebarOpen(!sidebarOpen)}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              onRangeSelect={handleRangeSelect}
              onClearSelection={handleClearSelection}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              trashMode={trashMode}
              onTrashToggle={handleTrashToggle}
              trashCount={trashCount}
              onTagRename={handleTagRename}
              onTagDelete={handleTagDelete}
              onTagMerge={handleTagMerge}
              onTagColor={handleTagColor}
              tagMetadata={tagMetadata}
              projectDocs={projectList}
              onProjectDocSelect={handleProjectDocSelect}
              activeProjectDoc={activeProjectDoc}
              onQuickOpen={(tab, query) => {
                setQuickOpenInitialTab(tab || null);
                setQuickOpenInitialQuery(query || '');
                setShowQuickOpen(true);
              }}
            />
          </div>
          {!isMobile && <ResizeHandle onResize={handleSidebarResize} onResizeEnd={handleResizeEnd} />}
        </>
      )}

      {/* Sidebar Toggle (when collapsed or mobile) */}
      {(!sidebarOpen || (isMobile && !sidebarOpen)) && (
        <button
          className="bear-sidebar-open-btn"
          onClick={() => setSidebarOpen(true)}
          title="사이드바 열기"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12h18M3 6h18M3 18h18" />
          </svg>
        </button>
      )}

      {/* Editor */}
      <div className="bear-editor-area">
        {activeProjectDoc ? (
          <ProjectDocViewer
            project={activeProjectDoc.project}
            docId={activeProjectDoc.id}
            onBack={() => navigate('/')}
          />
        ) : trashMode ? (
          <div className="bear-empty-state">
            <div className="bear-empty-icon">🗑</div>
            <p className="bear-empty-title">휴지통</p>
            <p className="bear-empty-subtitle">노트를 우클릭하여 복원하거나 영구 삭제할 수 있습니다</p>
          </div>
        ) : activeId ? (
          <Editor
            key={activeId}
            onSaved={handleDocumentSaved}
            onDelete={() => handleDelete(Number(activeId))}
            onPin={() => handlePin(Number(activeId))}
            onDuplicate={() => handleDuplicate(Number(activeId))}
            onExport={() => handleExport(Number(activeId), activeDoc?.title)}
            isPinned={activeDoc?.is_pinned}
            allTags={allTags}
          />
        ) : (
          <div className="bear-empty-state">
            <div className="bear-empty-icon">📝</div>
            <p className="bear-empty-title">노트를 선택하세요</p>
            <p className="bear-empty-subtitle">목록에서 노트를 선택하거나 새로 만드세요</p>
            <button onClick={handleNew} className="bear-empty-btn">
              새 노트
            </button>
          </div>
        )}
      </div>

      {/* Keyboard Shortcuts Button */}
      <button
        className="bear-shortcuts-fab"
        onClick={() => setShowShortcuts(true)}
        title="키보드 단축키 (?)"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="M6 8h.01M10 8h.01M14 8h.01M18 8h.01M8 12h.01M12 12h.01M16 12h.01M7 16h10" />
        </svg>
      </button>

      {/* Merge Dialog */}
      {showMergeDialog && selectedDocs.length >= 2 && (
        <MergeDialog
          documents={selectedDocs}
          onMerge={handleMerge}
          onClose={() => setShowMergeDialog(false)}
        />
      )}

      {/* Keyboard Shortcuts Modal */}
      {showShortcuts && (
        <KeyboardShortcutsModal onClose={() => setShowShortcuts(false)} />
      )}

      {/* Quick Open Modal */}
      {showQuickOpen && (
        <QuickOpenModal
          onClose={() => {
            setShowQuickOpen(false);
            setQuickOpenInitialTab(null);
            setQuickOpenInitialQuery('');
          }}
          onSelect={handleQuickOpenSelect}
          onProjectDocSelect={handleProjectDocSelect}
          initialTab={quickOpenInitialTab}
          initialQuery={quickOpenInitialQuery}
        />
      )}
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <NotificationProvider>
          <div className="min-h-screen bg-white">
            <NavigationBar />
            <main className="pt-11">
              <ProtectedRoute>
                <Routes>
                  <Route path="/" element={<BearLayout />} />
                  <Route path="/editor/:id" element={<BearLayout />} />
                  <Route path="/editor" element={<BearLayout />} />
                  <Route path="/project/:projectName" element={<BearLayout />} />
                  <Route path="/project/:projectName/:docId" element={<BearLayout />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </ProtectedRoute>
            </main>
          </div>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
